import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { APP_LOCALE } from "../../config/locale";
import { Modal } from "../../components/Modal";
import { formatCurrency } from "../Obras/components/FinancialDashboardShared";
import {
  createTaxObligation,
  deleteTaxObligation,
  getTaxObligations,
  markTaxObligationPaid,
  updateTaxObligation,
  type TaxObligation,
  type TaxObligationKind,
  type TaxObligationPayload,
} from "../../taxObligations/taxObligationsApi";

/** Exposto ao pai para pré-preencher e abrir o formulário (provisão de IVA sugerida). */
export interface TaxObligationsPanelHandle {
  openCreate: (prefill?: Partial<TaxObligationPayload>) => void;
}

const KIND_OPTIONS: { value: TaxObligationKind; label: string }[] = [
  { value: "iva", label: "IVA" },
  { value: "irc", label: "IRC" },
  { value: "irs", label: "IRS" },
  { value: "seguranca_social", label: "Segurança Social" },
  { value: "outro", label: "Outro" },
];

const KIND_LABELS: Record<TaxObligationKind, string> = {
  iva: "IVA",
  irc: "IRC",
  irs: "IRS",
  seguranca_social: "Segurança Social",
  outro: "Outro",
};

const dateFormatter = new Intl.DateTimeFormat(APP_LOCALE, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

function formatDate(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return dateFormatter.format(date);
}

function toDateInputValue(value?: string | null): string {
  if (!value) return "";
  return value.slice(0, 10);
}

interface ObligationStatus {
  label: string;
  className: string;
}

// Pago = verde; por pagar: vencida (data-limite passada) = vermelho, pendente = amarelo.
function obligationStatus(obligation: TaxObligation): ObligationStatus {
  if (obligation.isPaid) {
    return { label: "Pago", className: "bg-green-500/10 text-green-700" };
  }
  const due = new Date(obligation.dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (!Number.isNaN(due.getTime()) && due.getTime() < today.getTime()) {
    return { label: "Vencida", className: "bg-red-500/10 text-red-700" };
  }
  return { label: "Pendente", className: "bg-yellow-500/10 text-yellow-700" };
}

const inputClass =
  "w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary";
const inputStyle = {
  borderRadius: "var(--radius-button)",
  fontSize: "var(--text-base)",
} as const;
const labelClass = "block text-foreground mb-2";
const labelStyle = {
  fontSize: "var(--text-caption)",
  fontWeight: "var(--font-weight-semi-bold)",
} as const;

type ModalMode = "create" | "edit" | null;

export const TaxObligationsPanel = forwardRef<TaxObligationsPanelHandle>(
  function TaxObligationsPanel(_props, ref) {
    const [obligations, setObligations] = useState<TaxObligation[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);

    const [modalMode, setModalMode] = useState<ModalMode>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [formKind, setFormKind] = useState<TaxObligationKind>("iva");
    const [formDescription, setFormDescription] = useState("");
    const [formPeriodLabel, setFormPeriodLabel] = useState("");
    const [formAmount, setFormAmount] = useState("");
    const [formDueDate, setFormDueDate] = useState("");
    const [formError, setFormError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const [deleteTarget, setDeleteTarget] = useState<TaxObligation | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const load = useCallback(async () => {
      setIsLoading(true);
      setError(null);
      try {
        setObligations(await getTaxObligations());
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível carregar as obrigações fiscais.",
        );
      } finally {
        setIsLoading(false);
      }
    }, []);

    useEffect(() => {
      load();
    }, [load]);

    const resetForm = useCallback((prefill?: Partial<TaxObligationPayload>) => {
      setFormKind(prefill?.kind ?? "iva");
      setFormDescription(prefill?.description ?? "");
      setFormPeriodLabel(prefill?.periodLabel ?? "");
      setFormAmount(
        prefill?.amount !== undefined && prefill?.amount !== null
          ? String(prefill.amount)
          : "",
      );
      setFormDueDate(toDateInputValue(prefill?.dueDate ?? null));
      setFormError(null);
    }, []);

    const openCreate = useCallback(
      (prefill?: Partial<TaxObligationPayload>) => {
        setEditingId(null);
        resetForm(prefill);
        setModalMode("create");
      },
      [resetForm],
    );

    useImperativeHandle(ref, () => ({ openCreate }), [openCreate]);

    const openEdit = (obligation: TaxObligation) => {
      setEditingId(obligation.id);
      resetForm({
        kind: obligation.kind,
        description: obligation.description,
        periodLabel: obligation.periodLabel,
        amount: obligation.amount,
        dueDate: obligation.dueDate,
      });
      setModalMode("edit");
    };

    const closeModal = () => {
      setModalMode(null);
      setEditingId(null);
    };

    const handleSave = async () => {
      setFormError(null);
      if (!formDueDate) {
        setFormError("A data-limite é obrigatória.");
        return;
      }
      const amountNum = parseFloat(formAmount);
      if (formAmount === "" || Number.isNaN(amountNum) || amountNum < 0) {
        setFormError("O valor deve ser igual ou superior a 0.");
        return;
      }

      const payload: TaxObligationPayload = {
        kind: formKind,
        description: formDescription.trim() || null,
        periodLabel: formPeriodLabel.trim() || null,
        amount: amountNum,
        dueDate: formDueDate,
      };

      setIsSaving(true);
      try {
        if (modalMode === "edit" && editingId !== null) {
          await updateTaxObligation(editingId, payload);
        } else {
          await createTaxObligation(payload);
        }
        closeModal();
        await load();
      } catch (err) {
        setFormError(
          err instanceof Error
            ? err.message
            : "Não foi possível guardar a obrigação.",
        );
      } finally {
        setIsSaving(false);
      }
    };

    const handleMarkPaid = async (obligation: TaxObligation) => {
      setBusyId(obligation.id);
      setError(null);
      try {
        await markTaxObligationPaid(obligation.id);
        await load();
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível marcar como paga.",
        );
      } finally {
        setBusyId(null);
      }
    };

    const handleDelete = async () => {
      if (!deleteTarget) return;
      setIsDeleting(true);
      setError(null);
      try {
        await deleteTaxObligation(deleteTarget.id);
        setDeleteTarget(null);
        await load();
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível apagar a obrigação.",
        );
      } finally {
        setIsDeleting(false);
      }
    };

    const actionButtonClass =
      "inline-flex items-center justify-center px-3 py-1.5 min-h-[44px] border border-border bg-card text-foreground cursor-pointer transition-colors hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed text-sm";

    return (
      <div
        className="bg-card border border-border"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 md:px-5 py-4 border-b border-border">
          <div>
            <h2
              className="text-foreground"
              style={{
                fontSize: "var(--text-h3)",
                fontWeight: "var(--font-weight-semi-bold)",
              }}
            >
              Obrigações fiscais
            </h2>
            <p className="text-secondary-text text-sm mt-1">
              IVA, IRC, Segurança Social e outras obrigações a pagar. Entram
              automaticamente na Previsão de Tesouraria.
            </p>
          </div>
          <button
            type="button"
            onClick={() => openCreate()}
            className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            Nova obrigação
          </button>
        </div>

        {error && (
          <div
            className="m-4 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
            style={{ borderRadius: "var(--radius)" }}
          >
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full" style={{ minWidth: 760 }}>
            <thead>
              <tr className="border-b border-border bg-muted text-left text-secondary-text text-sm">
                <th className="px-3 py-3">Tipo</th>
                <th className="px-3 py-3">Período</th>
                <th className="px-3 py-3 text-right">Valor</th>
                <th className="px-3 py-3">Data-limite</th>
                <th className="px-3 py-3">Estado</th>
                <th className="px-3 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-center text-secondary-text"
                  >
                    A carregar…
                  </td>
                </tr>
              ) : obligations.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-center text-secondary-text"
                  >
                    Sem obrigações registadas.
                  </td>
                </tr>
              ) : (
                obligations.map((obligation) => {
                  const status = obligationStatus(obligation);
                  return (
                    <tr
                      key={obligation.id}
                      className="border-b border-border last:border-b-0"
                    >
                      <td className="px-3 py-3 text-foreground">
                        <div style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
                          {KIND_LABELS[obligation.kind] ?? obligation.kind}
                        </div>
                        {obligation.description && (
                          <div className="text-secondary-text text-xs mt-0.5">
                            {obligation.description}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-secondary-text">
                        {obligation.periodLabel ?? "—"}
                      </td>
                      <td className="px-3 py-3 text-right whitespace-nowrap text-foreground">
                        {formatCurrency(obligation.amount)}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {formatDate(obligation.dueDate)}
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-flex px-2 py-1 text-xs ${status.className}`}
                          style={{ borderRadius: "var(--radius-button)" }}
                        >
                          {status.label}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap justify-end gap-2">
                          {!obligation.isPaid && (
                            <button
                              type="button"
                              onClick={() => handleMarkPaid(obligation)}
                              disabled={busyId === obligation.id}
                              className={actionButtonClass}
                              style={{ borderRadius: "var(--radius-button)" }}
                            >
                              {busyId === obligation.id
                                ? "A marcar…"
                                : "Marcar paga"}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => openEdit(obligation)}
                            className={actionButtonClass}
                            style={{ borderRadius: "var(--radius-button)" }}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(obligation)}
                            className={actionButtonClass}
                            style={{ borderRadius: "var(--radius-button)" }}
                          >
                            Apagar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {modalMode && (
          <Modal
            title={
              modalMode === "edit" ? "Editar obrigação" : "Nova obrigação fiscal"
            }
            onClose={closeModal}
            footer={
              <>
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={isSaving}
                  className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors min-h-[44px]"
                  style={{ borderRadius: "var(--radius-button)" }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderRadius: "var(--radius-button)" }}
                >
                  {isSaving ? "A guardar…" : "Guardar"}
                </button>
              </>
            }
          >
            {formError && (
              <div
                className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
                style={{ borderRadius: "var(--radius)" }}
              >
                {formError}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="obligation-kind" className={labelClass} style={labelStyle}>
                  Tipo <span className="text-primary">*</span>
                </label>
                <select
                  id="obligation-kind"
                  value={formKind}
                  onChange={(event) =>
                    setFormKind(event.target.value as TaxObligationKind)
                  }
                  className={inputClass}
                  style={inputStyle}
                >
                  {KIND_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="obligation-period"
                  className={labelClass}
                  style={labelStyle}
                >
                  Período
                </label>
                <input
                  id="obligation-period"
                  type="text"
                  placeholder="Ex: 2026-T2, Junho 2026"
                  value={formPeriodLabel}
                  onChange={(event) => setFormPeriodLabel(event.target.value)}
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <div>
                <label
                  htmlFor="obligation-amount"
                  className={labelClass}
                  style={labelStyle}
                >
                  Valor a pagar (€) <span className="text-primary">*</span>
                </label>
                <input
                  id="obligation-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={formAmount}
                  onChange={(event) => setFormAmount(event.target.value)}
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <div>
                <label
                  htmlFor="obligation-due"
                  className={labelClass}
                  style={labelStyle}
                >
                  Data-limite <span className="text-primary">*</span>
                </label>
                <input
                  id="obligation-due"
                  type="date"
                  value={formDueDate}
                  onChange={(event) => setFormDueDate(event.target.value)}
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <div className="sm:col-span-2">
                <label
                  htmlFor="obligation-description"
                  className={labelClass}
                  style={labelStyle}
                >
                  Descrição
                </label>
                <input
                  id="obligation-description"
                  type="text"
                  placeholder="Notas (opcional)"
                  value={formDescription}
                  onChange={(event) => setFormDescription(event.target.value)}
                  className={inputClass}
                  style={inputStyle}
                />
              </div>
            </div>
          </Modal>
        )}

        {deleteTarget && (
          <Modal
            title="Apagar obrigação"
            onClose={() => setDeleteTarget(null)}
            maxWidthClass="max-w-md"
            footer={
              <>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(null)}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors min-h-[44px]"
                  style={{ borderRadius: "var(--radius-button)" }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-destructive/10 text-destructive border border-destructive/30 rounded transition-colors min-h-[44px] hover:bg-destructive/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderRadius: "var(--radius-button)" }}
                >
                  {isDeleting ? "A apagar…" : "Apagar"}
                </button>
              </>
            }
          >
            <p className="text-foreground">
              Tem a certeza que quer apagar a obrigação{" "}
              <span style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
                {KIND_LABELS[deleteTarget.kind] ?? deleteTarget.kind}
                {deleteTarget.periodLabel ? ` · ${deleteTarget.periodLabel}` : ""}
              </span>
              ? Esta ação não pode ser revertida.
            </p>
          </Modal>
        )}
      </div>
    );
  },
);
